/**
 * YUMIX — useMealDraft (Iter. 396 · Lot #12)
 *
 * Single source of truth for the multi-ingredient meal cart.
 * Centralizes the in-progress meal so that label scans, barcode scans,
 * AND text descriptions can all be COMBINED into one final meal before
 * submission — instead of each flow creating its own isolated meal.
 *
 * Persistence:
 *   - Every mutation is automatically mirrored to localStorage under
 *     `LS_KEY` (with a timestamp). Lets the user resume their cart if
 *     the camera times out, the tab is backgrounded, or the browser
 *     crashes mid-flow.
 *   - Drafts older than DRAFT_TTL_MS (24h) are silently dropped on load.
 *   - `wasRestored` flips to true once on the first mount when a stored
 *     draft was rehydrated → the caller can show a "Repas en cours
 *     restauré · Repartir à zéro" toast.
 *
 * Usage:
 *   const { items, addItems, removeItem, clear, wasRestored, ackRestored } = useMealDraft();
 *   addItems([{ id, name, calories, ... }, ...]);
 *
 * Notes:
 *   - `addItems` ALWAYS appends — never replaces. This is the whole point
 *     of the Lot #12 fix.
 *   - IDs are auto-assigned if missing.
 */
import { useState, useEffect, useCallback, useRef } from 'react';

const LS_KEY = 'yumix_meal_draft_v1';
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24h

function readPersisted() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items) || parsed.items.length === 0) return null;
    const ageMs = Date.now() - (parsed.savedAt || 0);
    if (ageMs > DRAFT_TTL_MS) return null;
    return parsed.items;
  } catch (_) {
    return null;
  }
}

function writePersisted(items) {
  try {
    if (!items || items.length === 0) {
      localStorage.removeItem(LS_KEY);
      return;
    }
    localStorage.setItem(LS_KEY, JSON.stringify({ items, savedAt: Date.now() }));
  } catch (_) { /* private mode / quota — non-blocking */ }
}

let _idCounter = 0;
const nextId = () => `draft_${Date.now()}_${++_idCounter}`;

export function useMealDraft() {
  const [items, setItems] = useState(() => readPersisted() || []);
  const [wasRestored, setWasRestored] = useState(() => {
    // Only flip true if there were persisted items at mount AND we didn't
    // just create an empty array. Used by the host page to fire the toast.
    return Boolean(readPersisted());
  });
  // We re-run persistence on every items mutation. A ref tracks whether
  // we've already done the initial mount so we don't re-write the same
  // data we just read.
  const skipInitialWrite = useRef(true);

  useEffect(() => {
    if (skipInitialWrite.current) {
      skipInitialWrite.current = false;
      return;
    }
    writePersisted(items);
  }, [items]);

  /** Append items (single or array). Auto-assigns IDs if missing. */
  const addItems = useCallback((newItems) => {
    const arr = Array.isArray(newItems) ? newItems : [newItems];
    if (arr.length === 0) return;
    setItems(prev => [...prev, ...arr.map(it => ({ ...it, id: it.id ?? nextId() }))]);
  }, []);

  /** Remove by id. */
  const removeItem = useCallback((id) => {
    setItems(prev => prev.filter(it => it.id !== id));
  }, []);

  /** Replace the entire cart (rare — used for explicit "discard" then re-fill). */
  const replaceItems = useCallback((arr) => {
    setItems(Array.isArray(arr) ? arr.map(it => ({ ...it, id: it.id ?? nextId() })) : []);
  }, []);

  /** Empty cart + clear persistence. Called after a successful submit. */
  const clear = useCallback(() => {
    setItems([]);
    writePersisted([]);
  }, []);

  /** Caller acknowledges the restored toast — flip the flag off. */
  const ackRestored = useCallback(() => setWasRestored(false), []);

  return {
    items,
    addItems,
    removeItem,
    replaceItems,
    clear,
    wasRestored,
    ackRestored,
  };
}
